import * as React from 'react';

import Routes from 'routing/Routes';
import { DEFAULT_TIMERANGE } from 'views/Constants';
import SearchLink from 'views/logic/search/SearchLink';
import { Spinner } from 'components/common';
import usePermissions from 'hooks/usePermissions';
import { createElasticsearchQueryString } from 'views/logic/queries/Query';
import { useGetSecurityEvent } from 'security-app/hooks/useSecurityEventsAPI';
import type { AbsoluteTimeRange } from 'views/logic/queries/Query';
import type { EventProcedureStepAPIType } from 'security-app/hooks/api/eventProceduresAPI.types';

import StepActionButton from './StepActionButton';

type Props = {
  eventId?: string;
  config: EventProcedureStepAPIType['action']['config'];
};

function StepPerformSearch({ config, eventId = undefined }: Props) {
  const { use_saved_search, query, saved_search, parameters } = config;
  const { securityEvent, loadingSecurityEvents } = useGetSecurityEvent(eventId);

  const { isPermitted } = usePermissions();
  const disabled = saved_search && !isPermitted(`view:read:${saved_search}`);

  if (eventId && loadingSecurityEvents) return <Spinner text="" />;

  const buildSavedSearchLink = () => {
    const queryString = Object.entries(parameters)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    const baseLink = Routes.VIEWS.VIEWID(saved_search);
    const timeRangeParams = securityEvent?.event?.replay_info
      ? `rangetype=absolute&from=${securityEvent?.event?.replay_info.timerange_start}&to=${securityEvent?.event?.replay_info.timerange_end}`
      : '';

    return `${baseLink}?${queryString ? `${queryString}&` : ''}${timeRangeParams}`;
  };

  const buildDynamicSearchLink = () => {
    const searchTimerange: AbsoluteTimeRange = {
      type: 'absolute',
      from: securityEvent?.event?.replay_info?.timerange_start,
      to: securityEvent?.event?.replay_info?.timerange_end,
    };

    return SearchLink.builder()
      .timerange(securityEvent?.event ? searchTimerange : DEFAULT_TIMERANGE)
      .query(createElasticsearchQueryString(query))
      .build()
      .toURL();
  };

  const link = use_saved_search ? buildSavedSearchLink() : buildDynamicSearchLink();

  return (
    <StepActionButton
      hint={disabled ? "You don't have access to see this saved search" : undefined}
      title="Perform Search"
      to={disabled ? null : link}
      disabled={disabled}
    />
  );
}

export default StepPerformSearch;
